/**
 * Tasks Page
 *
 * Displays all task instances with filtering, sorting, and drawer editing.
 */

import { useState, useCallback, useEffect, lazy, Suspense, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Space, Button, Segmented, message, Spin } from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { instancesApi, categoriesApi, stagesApi } from '@api/index';
import { TasksGrid, type ActiveFilter } from '@components/grid';
import { WorkPackageDrawer } from '@components/drawers';
import { SavedViewsDropdown } from '@components/views';
import type { ProcessInstance } from '@/types';

// Lazy load heavy view components
const OdooStyleKanbanBoard = lazy(() => import('@components/kanban/OdooStyleKanbanBoard'));
const GanttView = lazy(() => import('@components/gantt/GanttView'));

// Loading fallback for lazy components
const ViewLoading = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
    <Spin size="large" />
  </div>
);

type ViewMode = 'list' | 'kanban' | 'gantt';

export default function TasksPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get initial task ID from URL
  const initialTaskId = searchParams.get('id');

  // Parse saved view filters from URL
  const initialFilters = useMemo((): ActiveFilter[] => {
    const filtersParam = searchParams.get('filters');
    if (!filtersParam) return [];
    try {
      return JSON.parse(filtersParam);
    } catch {
      return [];
    }
  }, [searchParams]);

  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [drawerOpen, setDrawerOpen] = useState(!!initialTaskId);
  const [selectedTaskId, setSelectedTaskId] = useState<string | undefined>(initialTaskId || undefined);
  const [drawerMode, setDrawerMode] = useState<'view' | 'edit' | 'create'>('view');

  // Fetch all tasks - filtering is handled in grid toolbar now
  const { data: tasks = [], isLoading, refetch } = useQuery({
    queryKey: ['instances', 'all'],
    queryFn: () =>
      instancesApi.list({
        includeCompleted: true,
      }),
  });

  // Fetch categories for filter
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.list(),
  });

  // Fetch stages for Kanban board
  const { data: stages = [] } = useQuery({
    queryKey: ['stages'],
    queryFn: () => stagesApi.list(),
  });

  // Project filter state for Kanban
  const [projectFilter, setProjectFilter] = useState<string | undefined>();

  // Current filters for saved views (as plain object)
  const currentFilters = useMemo(() => {
    const filters: Record<string, unknown> = {};
    initialFilters.forEach(f => {
      filters[f.field] = f.value;
    });
    return filters;
  }, [initialFilters]);

  // Handle applying saved view
  const handleApplyView = useCallback((filters: Record<string, unknown>) => {
    // Convert saved view filters to ActiveFilter format
    const activeFilters: ActiveFilter[] = Object.entries(filters).map(([field, value]) => ({
      field,
      value: value as string | [string, string] | null,
    }));
    setSearchParams({ filters: JSON.stringify(activeFilters) });
  }, [setSearchParams]);

  // Handle stage change (drag-drop)
  const handleStageChange = useCallback(async (taskId: string, newStageId: string) => {
    await stagesApi.moveTask(taskId, newStageId);
    queryClient.invalidateQueries({ queryKey: ['instances'] });
  }, [queryClient]);

  // Quick actions
  const startMutation = useMutation({
    mutationFn: (id: string) => instancesApi.start(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instances'] });
      message.success('Task started');
    },
    onError: () => message.error('Failed to start task'),
  });

  const completeMutation = useMutation({
    mutationFn: (id: string) => instancesApi.complete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instances'] });
      message.success('Task completed');
    },
    onError: () => message.error('Failed to complete task'),
  });

  // Create ad-hoc task
  const createTaskMutation = useMutation({
    mutationFn: () =>
      instancesApi.create({
        title: 'Untitled Task',
        dueDate: new Date().toISOString().split('T')[0],
      }),
    onSuccess: (newTask) => {
      queryClient.invalidateQueries({ queryKey: ['instances'] });
      setSelectedTaskId(newTask.id);
      setDrawerMode('edit');
      setDrawerOpen(true);
      setSearchParams({ id: newTask.id });
    },
    onError: () => message.error('Failed to create task'),
  });

  const handleCreateTask = useCallback(() => {
    createTaskMutation.mutate();
  }, [createTaskMutation]);

  // Open task from URL parameter
  useEffect(() => {
    if (initialTaskId) {
      setSelectedTaskId(initialTaskId);
      setDrawerOpen(true);
      setDrawerMode('view');
    }
  }, [initialTaskId]);

  const handleViewTask = useCallback((task: ProcessInstance) => {
    setSelectedTaskId(task.id);
    setDrawerMode('view');
    setDrawerOpen(true);
    setSearchParams({ id: task.id });
  }, [setSearchParams]);

  const handleStartTask = useCallback(
    (task: ProcessInstance) => {
      startMutation.mutate(task.id);
    },
    [startMutation]
  );

  const handleCompleteTask = useCallback(
    (task: ProcessInstance) => {
      completeMutation.mutate(task.id);
    },
    [completeMutation]
  );

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedTaskId(undefined);
    setSearchParams({});
  }, [setSearchParams]);

  const handleDrawerSaved = useCallback(() => {
    refetch();
  }, [refetch]);

  // Extra toolbar buttons for view mode and refresh
  const extraButtons = useMemo(() => (
    <Space>
      <SavedViewsDropdown
        viewType="tasks"
        currentFilters={currentFilters}
        onApplyView={handleApplyView}
      />
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={handleCreateTask}
        loading={createTaskMutation.isPending}
      >
        New Task
      </Button>
      <Segmented
        value={viewMode}
        onChange={(val) => setViewMode(val as ViewMode)}
        options={[
          { value: 'list', icon: <UnorderedListOutlined /> },
          { value: 'kanban', icon: <AppstoreOutlined /> },
          { value: 'gantt', icon: <BarChartOutlined /> },
        ]}
      />
      <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isLoading} />
    </Space>
  ), [viewMode, isLoading, refetch, handleCreateTask, createTaskMutation.isPending, currentFilters, handleApplyView]);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Content area */}
      <div style={{ flex: 1 }}>
        {viewMode === 'list' && (
          <TasksGrid
            tasks={tasks}
            loading={isLoading}
            onViewTask={handleViewTask}
            onStartTask={handleStartTask}
            onCompleteTask={handleCompleteTask}
            categories={categories}
            extraToolbarButtons={extraButtons}
            initialFilters={initialFilters}
          />
        )}
        {viewMode === 'kanban' && (
          <>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
              {extraButtons}
            </div>
            <Suspense fallback={<ViewLoading />}>
              <OdooStyleKanbanBoard
                tasks={tasks}
                stages={stages.length > 0 ? stages : [
                  // Default stages if none configured
                  { id: 'backlog', name: 'Backlog', sequence: 0, color: '#6b7280' },
                  { id: 'todo', name: 'To Do', sequence: 10, color: '#f59e0b' },
                  { id: 'in_progress', name: 'In Progress', sequence: 20, color: '#3b82f6' },
                  { id: 'review', name: 'Review', sequence: 30, color: '#8b5cf6' },
                  { id: 'done', name: 'Done', sequence: 40, color: '#22c55e' },
                ]}
                loading={isLoading}
                onTaskClick={handleViewTask}
                onStageChange={handleStageChange}
                projectFilter={projectFilter}
                onProjectFilterChange={setProjectFilter}
              />
            </Suspense>
          </>
        )}
        {viewMode === 'gantt' && (
          <>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
              {extraButtons}
            </div>
            <Suspense fallback={<ViewLoading />}>
              <GanttView
                tasks={tasks}
                loading={isLoading}
                onTaskClick={handleViewTask}
              />
            </Suspense>
          </>
        )}
      </div>

      {/* Work Package Drawer */}
      <WorkPackageDrawer
        open={drawerOpen}
        itemId={selectedTaskId}
        mode={drawerMode}
        isTemplate={false}
        onClose={handleCloseDrawer}
        onSaved={handleDrawerSaved}
      />
    </div>
  );
}
